<template>
  <a-modal
    :visible="visible"
    :confirm-loading="loading"
    :footer="null"
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">신용카드 등록</div>
    </template>
    
    <div class="modal-content">
      <a-form :model="form" layout="vertical">
        <!-- 카드번호 입력 필드 -->
        <a-form-item label="카드번호" name="cardNumber">
          <a-input 
            v-model:value="form.cardNumber" 
            placeholder="0000-0000-0000-0000" 
            :maxlength="19"
            @input="formatCardNumber"
          />
          <div class="description-text">국내 신용카드 번호 16자리(또는 15자리)를 입력해주세요.</div>
        </a-form-item>
        
        <!-- 유효기간 필드 -->
        <a-form-item label="유효기간" name="cardExpiry">
          <div class="card-expiry-container">
            <a-input 
              v-model:value="form.cardExpiryMonth" 
              placeholder="MM" 
              :maxlength="2"
              class="expiry-input"
              @input="allowNumbersOnly"
            />
            <span class="expiry-separator">/</span>
            <a-input 
              v-model:value="form.cardExpiryYear" 
              placeholder="YY" 
              :maxlength="2"
              class="expiry-input"
              @input="allowNumbersOnly"
            />
          </div>
        </a-form-item>
        
        <!-- 생년월일 6자리 필드 -->
        <a-form-item label="생년월일(6자리) / 사업자번호(10자리)" name="birthDate">
          <a-input 
            v-model:value="form.birthDate" 
            placeholder="230131 또는 123-45-67890" 
            :maxlength="12"
            @input="formatBirthDate"
          />
          <div class="description-text">법인카드의 경우 사업자등록번호 10자리를 입력해주세요.</div>
        </a-form-item>
        
        <!-- 카드 비밀번호 앞 2자리 필드 -->
        <a-form-item label="카드 비밀번호 앞 2자리" name="cardPassword">
          <a-input-password 
            v-model:value="form.cardPassword" 
            placeholder="••" 
            :maxlength="2"
            @input="allowNumbersOnly"
          />
        </a-form-item>
        
        <div class="modal-footer">
          <a-button @click="handleCancel">취소</a-button>
          <a-button 
            type="primary" 
            :loading="loading"
            @click="handleSubmit"
            class="yellow-button"
          >등록</a-button>
        </div>
      </a-form>
    </div>
  </a-modal>
</template>

<script>
import { ref, reactive } from 'vue';
import { getCurrentInstance } from 'vue';
import dayjs from 'dayjs';
import salesRecordsApis from './api';

export default {
  name: 'AddCardModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    subscription: {
      type: Object,
      default: () => ({})
    }
  },
  
  emits: ['update:visible', 'card-added'],
  
  setup(props, { emit }) {
    const internalInstance = getCurrentInstance();
    const globalProperties = internalInstance?.appContext.config.globalProperties;
    
    // 상태 정의
    const loading = ref(false);
    
    // 폼 데이터
    const form = reactive({
      cardNumber: '',
      cardExpiryMonth: '',
      cardExpiryYear: '',
      birthDate: '',
      cardPassword: '',
    });
    
    // 토스트 메시지 표시 유틸리티 함수
    const showToast = (type, message) => {
      if (globalProperties?.$message) {
        globalProperties.$message[type](message);
      } else {
        alert(message);
      }
    };
    
    // 숫자만 입력 허용 함수
    const allowNumbersOnly = (e) => {
      if (e.target.value) {
        e.target.value = e.target.value.replace(/\D/g, '');
      }
    };
    
    // 카드번호 포맷팅 함수
    const formatCardNumber = (e) => {
      let value = e.target.value.replace(/\D/g, '');
      let formattedValue = '';
      
      // 아멕스(American Express) 카드 체크 (34, 37로 시작)
      if (value.startsWith('34') || value.startsWith('37')) {
        if (value.length > 15) {
          value = value.slice(0, 15);
        }
        
        formattedValue = value.slice(0, 4);
        if (value.length > 4) {
          formattedValue += '-' + value.slice(4, 10);
        }
        if (value.length > 10) {
          formattedValue += '-' + value.slice(10, 15);
        }
      } else {
        // 일반 카드 (16자리)
        if (value.length > 16) {
          value = value.slice(0, 16);
        }
        
        for (let i = 0; i < value.length; i += 4) {
          if (i > 0) {
            formattedValue += '-';
          }
          formattedValue += value.slice(i, Math.min(i + 4, value.length));
        }
      }
      
      form.cardNumber = formattedValue;
    };
    
    // 생년월일/사업자번호 입력 핸들러
    const formatBirthDate = (e) => {
      let value = e.target.value.replace(/\D/g, '');
      
      // 6자리 이하는 생년월일로 간주하고 그대로 유지 (하이픈 없음)
      if (value.length <= 6) {
        form.birthDate = value;
        return;
      }
      
      // 6자리 초과는 사업자번호로 간주하고 포맷팅 적용
      let formattedValue = '';
      
      // 앞 3자리
      formattedValue = value.slice(0, 3);
      
      // 중간 2자리
      if (value.length > 3) {
        formattedValue += '-' + value.slice(3, 5);
        
        // 뒤 5자리
        if (value.length > 5) {
          formattedValue += '-' + value.slice(5);
        }
      }
      
      // 최종 값 할당 (최대 10자리 숫자 + 2개 하이픈)
      form.birthDate = formattedValue;
    };
    
    // 폼 유효성 검사
    const validateForm = () => {
      // 카드번호 검증
      if (!form.cardNumber) {
        showToast('warning', '카드 번호를 입력해주세요.');
        return false;
      }
      
      // 카드번호 형식 검증 (하이픈 제외 숫자만)
      const cardNumberDigits = form.cardNumber.replace(/-/g, '');
      if (!(cardNumberDigits.length === 15 || cardNumberDigits.length === 16)) {
        showToast('warning', '유효한 카드번호를 입력해주세요.');
        return false;
      }
      
      // 유효기간 검증
      if (!form.cardExpiryMonth || !form.cardExpiryYear) {
        showToast('warning', '카드 유효기간을 입력해주세요.');
        return false;
      }
      
      // 유효기간 형식 검증
      const month = parseInt(form.cardExpiryMonth, 10);
      const year = parseInt(form.cardExpiryYear, 10);
      
      if (month < 1 || month > 12) {
        showToast('warning', '유효한 월(01~12)을 입력해주세요.');
        return false;
      }
      
      // 유효기간 만료 검증
      const currentYear = dayjs().year() % 100; // 현재 연도의 뒤 두 자리
      const currentMonth = dayjs().month() + 1; // 현재 월 (1~12)
      
      if (year < currentYear || (year === currentYear && month < currentMonth)) {
        showToast('warning', '카드가 이미 만료되었습니다. 유효한 카드를 사용해주세요.');
        return false;
      }
      
      // 생년월일/사업자번호 검증
      if (!form.birthDate) {
        showToast('warning', '생년월일(또는 사업자등록번호)을 입력해주세요.');
        return false;
      }
      
      // 비밀번호 검증 (선택적)
      if (form.cardPassword && form.cardPassword.length !== 2) {
        showToast('warning', '카드 비밀번호 앞 2자리를 입력해주세요.');
        return false;
      }
      
      return true;
    };
    
    // 모달 취소 핸들러
    const handleCancel = () => {
      resetForm();
      emit('update:visible', false);
    };
    
    // 폼 리셋
    const resetForm = () => {
      form.cardNumber = '';
      form.cardExpiryMonth = '';
      form.cardExpiryYear = '';
      form.birthDate = '';
      form.cardPassword = '';
    };
    
    // 폼 제출 핸들러
    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }
      
      loading.value = true;
      
      try {
        const api = salesRecordsApis(globalProperties?.$apiInstance, globalProperties?.$store);
        
        // 카드 정보 구성 - 백엔드 AddCardCenterflowDto와 일치하는 형식
        const cardInfo = {
          cardNumber: form.cardNumber, // 원본 형식 유지 (하이픈 포함)
          cardExpiry: `${form.cardExpiryMonth}/${form.cardExpiryYear}`,
          birthDate: form.birthDate,
          cardPassword: form.cardPassword || '',
          isDefault: false, // 새 카드는 기본 카드가 아님
          isActive: true // 새 카드는 기본적으로 활성화
        };
        
        // 센터플로우 구독에 카드 등록 API 호출
        await api.addCenterflowCardInfo(
          props.subscription.id,
          cardInfo,
          (response) => {
            showToast('success', '카드가 성공적으로 등록되었습니다.');
            resetForm();
            emit('update:visible', false);
            emit('card-added');
          },
          (error) => {
            showToast('error', error.response?.data?.message || '카드 등록에 실패했습니다.');
          }
        );
      } catch (error) {
        showToast('error', '카드 등록 중 오류가 발생했습니다.');
      } finally {
        loading.value = false;
      }
    };
    
    return {
      loading,
      form,
      formatCardNumber,
      allowNumbersOnly,
      formatBirthDate,
      handleCancel,
      handleSubmit
    };
  }
};
</script>

<style scoped lang="scss">
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

.modal-content {
  padding: 20px;
  border: none;
  background: #fff;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

.description-text {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
}

.card-expiry-container {
  display: flex;
  align-items: center;
}

.expiry-input {
  width: 60px;
  text-align: center;
}

.expiry-separator {
  margin: 0 8px;
  font-size: 18px;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
